/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Select, FieldGroup, Form, SvgIcon, AsyncSelect } from "core/components";
import {
  InventoryItemFormData,
  ProjectFormData,
  ProjectSchema,
  ProjectStatus,
  ProjectType,
  TemplateFormData,
  TemplateStatus,
  UnitType,
  UserFormData
} from "core/schema";
import {
  admin_project_path,
  admin_projects_path,
  print_admin_project_path,
  edit_admin_project_path,
  admin_templates_path,
  admin_inventory_items_path
} from "routes";
import { navigateTo } from "core/navigation";
import { IMaskInput } from "react-imask";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.scss";
import { toDataUrl, toRangeOptions } from "core/helpers";
import ProjectPropertiesForm from "./ProjectPropertiesForm";
import ProjectParticularsForm from "./ProjectParticularsForm";
import ProjectTasksForm from "./ProjectTasksForm";
import ProjectCostBudget from "./ProjectCostBudget";
import ProjectRequisitions from "./ProjectRequisitions";
import ProjectParticulars from "./ProjectParticulars";
import ProjectChangeOrders from "./ProjectChangeOrders";
import ProjectChangeOrderItems from "./ProjectChangeOrderItems";
import ProjectChangeOrderRequisitions from "./ProjectChangeOrderRequisitions";
import ProjectPayrolls from "./ProjectPayrolls";
import ProjectReturns from "./ProjectReturns";
import AttentionModal from "./AttentionModal";

type ForemanOption = {
  id: number;
  first_name: string;
  last_name: string;
  active: boolean;
};

type ProjectFormProps = {
  project: ProjectFormData;
  templates: Array<TemplateFormData>;
  inventory_items: Array<InventoryItemFormData>;
  users: Array<UserFormData>;
  foremen: Array<ForemanOption>;
};

enum ProjectFormTabs {
  Main = 0,
  Properties = 1,
  Particulars = 2,
  Tasks = 3,
  CostBudget = 4,
  Requisitions = 5
}

const ProjectForm = ({
  project,
  project_types,
  unit_types,
  finance_types,
  sources,
  states,
  users,
  foremen,
  warehouses,
  allocation_rules,
  particular_types,
  task,
  can_delete,
  can_request,
  can_edit,
  is_admin_or_architect,
  is_engineer
}: ProjectFormProps): JSX.Element => {
  const formMethods = useForm<ProjectFormData>({
    mode: "onChange",
    defaultValues: ProjectSchema.cast(project),
    resolver: yupResolver(ProjectSchema)
  });
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    resetField,
    trigger,
    getValues,
    formState: { isSubmitting, isValidating, errors }
  } = formMethods;
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [templates, setTemplates] = React.useState([]);
  const [inventory_items, setInventoryItems] = React.useState([]);
  const [projectTasks, setProjectTasks] = React.useState(project.project_tasks);
  // const [isTrue, setIsTrue] = React.useState(is_true);
  // const [showModal, setShowModal] = React.useState(false);
  // React.useEffect(() => {
  //   setIsTrue(is_true);
  // }, [is_true]);

  // React.useEffect(() => {
  //   if (isTrue === 1) {
  //     setShowModal(true);
  //   } else {
  //     setShowModal(false);
  //   }
  // }, [isTrue]);
  React.useEffect(() => {}, []);

  const [isInventoryLoading, setIsInventoryLoading] = React.useState(false);
  const { policy } = project;
  const can_approve_reject = policy.approve_material_request || policy.approve_purchase_request;
  const [selectedTemplate, setSelectedTemplate] = React.useState(project?.template);
  const [blocks, setBlocks] = React.useState(toRangeOptions(project?.subdivision?.block_start, project?.subdivision?.block_end));
  const [lots, setLots] = React.useState(toRangeOptions(project?.subdivision?.lot_start, project?.subdivision?.lot_end));
  const unitType = watch("unit_type");
  const projectType = watch("project_type");
  const block = watch("block");
  const phase = watch("phase");
  const lot = watch("lot");
  const [selectedTab, setSelectedTab] = React.useState(ProjectFormTabs.Main);
  const [showCompleteConfirmationModal, setShowCompleteConfirmationModal] = React.useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = React.useState(false);
  const [showPendingConfirmationModal, setShowPendingConfirmationModal] = React.useState(false);
  const [showOngoingConfirmationModal, setShowOngoingConfirmationModal] = React.useState(false);
  const [showPunchlistingConfirmationModal, setShowPunchlistingConfirmationModal] = React.useState(false);
  const completion = parseFloat(project.completion);
  const canComplete = completion == 100.0 && policy.can_complete;
  const mainInfoFields = [
    "template_id",
    "code",
    "phase",
    "block",
    "lot",
    "project_type",
    "unit_type",
    "customer_first_name",
    "customer_last_name",
    "equity",
    "finance_type"
  ];
  const propertiesFields = [
    "manager_id",
    "architect_id",
    "engineer_id",
    "foreman_id",
    "warehouse_id",
    "allocation_rule_id",
    "authorized_by_design_id",
    "authorized_by_sales_id",
    "authorized_by_finance_id",
    "authorized_by_manager_id",
    "documents_attributes"
  ];
  const [mainInfoHasErrors, setMainInfoHasErrors] = React.useState(false);
  const [propertiesHasErrors, setPropertiesHasErrors] = React.useState(false);
  const [particularsHasErrors, setParticularsHasErrors] = React.useState(false);
  const getTemplates = async (code = "") => {
    const template_id = project?.template_id;
    const page = 1;
    const per = 10;

    try {
      const response = await api.get(admin_templates_path({ format: "json", template_id, page, per, code }));
      setTemplates(response.data);
      return response.data;
    } catch (error) {
      console.error(error);
    }
  };

  const getInventoryItems = async () => {
    try {
      setIsInventoryLoading(true);
      const response = await api.get(admin_inventory_items_path({ format: "json", warehouse_id: project.warehouse_id }));
      setInventoryItems(response.data);
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setIsInventoryLoading(false);
    }
  };

  const loadTemplateOptions = useAsyncDebounce(getTemplates, 400);

  React.useEffect(() => {
    getInventoryItems().catch(console.error);
  }, []);

  // ✅ Fetch project details before modal actions
  const fetchProjectDetails = async (id: number) => {
    try {
      const response = await api.get(`/admin/projects/${id}.json`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch project details", error);
      return null;
    }
  };

  const save = async (data: ProjectFormData): Promise<unknown> => {
    try {
      let response = null;

      if (data.id) {
        response = await api.put(admin_project_path({ id: project.id }), data);
      } else {
        response = await api.post(admin_projects_path({ id: project.id }), data);
      }

      navigateTo(edit_admin_project_path(response?.data?.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);
    setMainInfoHasErrors(mainInfoFields.some((field) => Object.keys(data).indexOf(field) >= 0));
    setPropertiesHasErrors(propertiesFields.some((field) => Object.keys(data).indexOf(field) >= 0));
    setParticularsHasErrors(data?.project_tasks_attributes);

    if (data?.particulars_attributes) {
      setFormErrors([data.particulars_attributes.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const documentsFromTemplate = async (project: ProjectFormData, template: TemplateFormData) => {
    return await Promise.all(
      project.documents_attributes.map(async (projectDocument) => {
        const { version } = projectDocument;
        const templateDocument = template.documents_attributes?.find(
          (templateDocument) => templateDocument.document_type == projectDocument.document_type
        );

        if (templateDocument) {
          const { document_type, file_name, file_url, description, remarks } = templateDocument;
          const document_data_uri = await toDataUrl(file_url);

          return {
            document_type,
            document_data_uri,
            file_name,
            version,
            description,
            remarks
          };
        }

        return projectDocument;
      })
    );
  };

  const projectActions = () => {
    if (project.status == ProjectStatus.Pending) {
      return (
        <>
          <Button
            className="mr-3 button--brand"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="save"
            title="Save"
            onClick={() => {
              setValue("status", ProjectStatus.Pending);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
          <Button
            className="mr-3 button--success"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="on-going"
            title="Mark as On-going"
            onClick={async () => {
              setValue("status", ProjectStatus.OnGoing);
              const result = await trigger([...mainInfoFields, ...propertiesFields], { shouldFocus: true });
              if (result) {
                setShowOngoingConfirmationModal(!showOngoingConfirmationModal);
              } else {
                setFormErrors(["There are errors on your form."]);
              }
            }}
          />
        </>
      );
    } else if (project.status == ProjectStatus.OnGoing) {
      return (
        <>
          <Button
            className="mr-3 button--brand"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="save"
            title="Save"
            onClick={() => {
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
          <Button
            className="mr-3 button--success"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="on-going"
            title="Submit for Punchlist"
            onClick={async () => {
              setValue("status", ProjectStatus.Punchlisting);

              // ✅ Validate form first
              const result = await trigger([...mainInfoFields, ...propertiesFields], { shouldFocus: true });
              if (!result) {
                setFormErrors(["There are errors on your form."]);
                return;
              }

              // ✅ Fetch live project data before showing modal
              const projectData = await fetchProjectDetails(project.id);

              if (projectData?.has_open_transactions) {
                project.open_transactions = true;
                project.open_transaction_message = projectData.open_transaction_message;
              } else {
                project.open_transactions = false;
                project.open_transaction_message = null;
              }

              setShowPunchlistingConfirmationModal(true);
            }}
          />
        </>
      );
    } else if (project.status == ProjectStatus.Draft) {
      return (
        <>
          <Button
            className="mr-3 button--brand"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="save"
            title="Save"
            onClick={() => {
              setValue("status", ProjectStatus.Draft);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
          <Button
            className="mr-3 button--pending"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="pending"
            title="Save as Pending"
            onClick={async () => {
              setValue("status", ProjectStatus.Pending);
              const result = await trigger([...mainInfoFields, ...propertiesFields], { shouldFocus: true });

              if (result) {
                setShowPendingConfirmationModal(!showPendingConfirmationModal);
              } else {
                setFormErrors(["There are errors on your form."]);
              }
            }}
          />
        </>
      );
    } else if (project.status == ProjectStatus.Punchlisting) {
      return (
        <>
          <Button
            className="mr-3 button--brand"
            loading={isSubmitting || isValidating}
            disabled={isSubmitting || isValidating}
            icon="save"
            title="Save"
            onClick={() => {
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
          {canComplete && (
            <Button
              className="mr-3 button--success"
              loading={isSubmitting || isValidating}
              disabled={isSubmitting || isValidating}
              icon="completed"
              title="Complete Project"
              onClick={async () => {
                setValue("status", ProjectStatus.Completed);
                const result = await trigger([...mainInfoFields, ...propertiesFields], { shouldFocus: true });
                if (result) {
                  setShowCompleteConfirmationModal(!showCompleteConfirmationModal);
                } else {
                  setFormErrors(["There are errors on your form."]);
                }
              }}
            />
          )}
        </>
      );
    } else if (project.status == ProjectStatus.Completed) {
      return (
        <Button
          className="mr-3 button--brand"
          loading={isSubmitting || isValidating}
          disabled={isSubmitting || isValidating}
          icon="save"
          title="Save"
          onClick={() => {
            handleSubmit(async (data) => await save(data), onError)();
          }}
        />
      );
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...formMethods}>
        <Form errors={formErrors}>
          {[ProjectStatus.OnGoing, ProjectStatus.Punchlisting].includes(project.status) && policy.can_cancel && (
            <Button
              className="mr-3 button--danger button--cancel-project"
              loading={isSubmitting || isValidating}
              disabled={isSubmitting || isValidating}
              icon="times"
              title="Cancel Project"
              onClick={async () => {
                setValue("status", ProjectStatus.Cancelled);
                const result = await trigger([...mainInfoFields, ...propertiesFields], { shouldFocus: true });
                if (result) {
                  setShowCancelConfirmationModal(!showCancelConfirmationModal);
                } else {
                  setFormErrors(["There are errors on your form."]);
                }
              }}
            />
          )}
          <Container fluid={true}>
            <Row className="mb-3">
              <Col className="d-flex justify-content-end">
                <Button
                  className="button--default"
                  disabled={isSubmitting || isValidating}
                  icon="arrow-left"
                  title="Back"
                  type="button"
                  onClick={() => navigateTo(admin_projects_path())}
                />
              </Col>
            </Row>
          </Container>
          <Tabs
            className="card"
            selectedTabClassName="project-tab__link project-tab__link--active"
            onSelect={(index) => {
              setSelectedTab(index);
            }}
          >
            <Container fluid={true}>
              <Row className="d-flex align-items-baseline">
                <Col>
                  <TabList className="card__header react-tabs__tab-list" style={{ padding: 0 }}>
                    <Col className="d-flex justify-content-start">
                      <Tab className="project-tab__link">
                        <>Main Info {mainInfoHasErrors && <SvgIcon name="exclamation" className="h4" style={{ color: "#f46a6a" }} />}</>
                      </Tab>
                      <Tab className="project-tab__link">
                        <>Properties {propertiesHasErrors && <SvgIcon name="exclamation" className="h4" style={{ color: "#f46a6a" }} />}</>
                      </Tab>
                      <Tab className="project-tab__link">
                        <>Particulars {particularsHasErrors && <SvgIcon name="exclamation" className="h4" style={{ color: "#f46a6a" }} />}</>
                      </Tab>
                      {project.status != ProjectStatus.Draft && (
                        <>
                          <Tab className="project-tab__link">Tasks</Tab>
                          <Tab className="project-tab__link">Cost Budget</Tab>
                          <Tab className="project-tab__link">Requisitions</Tab>
                          <Tab className="project-tab__link">Returns</Tab>
                          <Tab className="project-tab__link">Change Orders</Tab>
                          <Tab className="project-tab__link">C.O Items</Tab>
                          <Tab className="project-tab__link">C.O Req.</Tab>
                          <Tab className="project-tab__link">Payroll</Tab>
                        </>
                      )}
                    </Col>
                  </TabList>
                </Col>
                <Col sm={12} className="d-flex justify-content-end">
                  <div className="grid w-100">
                    <div className="text-right tile">
                      {selectedTab < ProjectFormTabs.Tasks && policy.can_save && projectActions()}

                      {[
                        ProjectStatus.Draft,
                        ProjectStatus.Pending,
                        ProjectStatus.OnGoing,
                        ProjectStatus.Completed,
                        ProjectStatus.Punchlisting
                      ].includes(project.status) &&
                        project.id && (
                          <Button
                            className="button--primary"
                            disabled={isSubmitting || isValidating}
                            icon="printer"
                            title="Print"
                            type="button"
                            onClick={() => window.open(print_admin_project_path(project.id))}
                          />
                        )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
            <TabPanel>
              <Card.Body>
                <Container fluid={true}>
                  <Row>
                    <Col>
                      <Row>
                        <Col>
                          <Controller
                            name="template_id"
                            control={control}
                            render={({ field }) => (
                              <AsyncSelect
                                label="Template"
                                placeholder="Select a Template"
                                required
                                noOptionsMessage={() => "No available templates."}
                                defaultOptions={true}
                                loadOptions={loadTemplateOptions}
                                getOptionLabel={(option) => option.code}
                                getOptionValue={(option) => option.id}
                                isOptionDisabled={(option) => option.status != TemplateStatus.Active}
                                onChange={(option, actionMeta) => {
                                  field.onChange(option?.id);
                                  if (actionMeta.action == "select-option") {
                                    setSelectedTemplate(option);
                                    setValue("name", option.unit_model.name);
                                    setValue("description", option.description);
                                    setValue("subdivision_id", option.subdivision.id);
                                    setValue("unit_model_id", option.unit_model.id);
                                    setValue("phase", option.phase);

                                    setValue(
                                      "code",
                                      `${projectType === ProjectType.House ? "A" : "B"}-${option.subdivision.code}${option.phase.padStart(
                                        2,
                                        "0"
                                      )}${block.padStart(2, "0")}${lot.padStart(2, "0")}-000`
                                    );
                                    setBlocks(toRangeOptions(option.subdivision.block_start, option.subdivision.block_end));
                                    setLots(toRangeOptions(option.subdivision.lot_start, option.subdivision.lot_end));
                                    if (option.phase) {
                                      setValue("block", option.block);
                                      setValue("lot", option.lot);
                                    }
                                    if (unitType == UnitType.Standard) {
                                      documentsFromTemplate(project, option).then((documents) => {
                                        setValue("documents_attributes", documents);
                                      });
                                    }
                                  } else {
                                    setBlocks([]);
                                    setLots([]);
                                    setSelectedTemplate(null);
                                    resetField("documents_attributes");
                                  }
                                }}
                                ref={field.ref}
                                value={templates.find((templates) => templates.id == field.value) || selectedTemplate || ""}
                                errorMessage={errors?.template_id?.message}
                                disabled={project.has_normal_project_task_items}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Input {...register("name")} className="field" label="Project Name" placeholder="-" readOnly={true} />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            {...register("description")}
                            className="field"
                            label="Project Description"
                            placeholder="Ceiling improvements, Floor Plan Revision"
                            errorMessage={errors.description?.message}
                            readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            className="field"
                            label="Subdivision"
                            placeholder="-"
                            value={project?.subdivision?.name || selectedTemplate?.subdivision?.name || ""}
                            readOnly={true}
                          />
                        </Col>
                        <Col>
                          <Input
                            {...register("phase")}
                            className="field"
                            label="Phase"
                            required
                            placeholder="-"
                            onChange={(e) => {
                              const increment = getValues("code")?.split("-")?.pop() || "000";
                              setValue(
                                "code",
                                `${projectType === ProjectType.House ? "A" : "B"}-${project?.subdivision?.code || selectedTemplate?.subdivision?.code || ""
                                }${e.target.value.padStart(2, "0")}${block.padStart(2, "0")}${lot.padStart(2, "0")}-${increment}`
                              );
                            }}
                            maxLength={2}
                            errorMessage={errors.phase?.message}
                            readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Controller
                            name="block"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Block"
                                placeholder="Select Block"
                                required
                                options={blocks}
                                noOptionsMessage={() => "Select a Template first."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);
                                  setValue(
                                    "code",
                                    `${projectType === ProjectType.House ? "A" : "B"}-${selectedTemplate.subdivision.code}${phase.padStart(
                                      2,
                                      "0"
                                    )}${newOption?.value?.padStart(2, "0")}${lot.padStart(2, "0")}-000`
                                  );
                                }}
                                value={blocks.find((block) => block.value == field.value) || null}
                                errorMessage={errors.block?.message}
                                disabled={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="lot"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Lot"
                                placeholder="Select Lot"
                                required
                                options={lots}
                                noOptionsMessage={() => "Select a Template first."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);

                                  setValue(
                                    "code",
                                    `${projectType === ProjectType.House ? "A" : "B"}-${selectedTemplate.subdivision.code}${phase.padStart(
                                      2,
                                      "0"
                                    )}${block.padStart(2, "0")}${newOption?.value?.padStart(2, "0")}-000`
                                  );
                                }}
                                value={lots.find((lot) => lot.value == field.value) || null}
                                errorMessage={errors.lot?.message}
                                disabled={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            className="field"
                            label="Site Address"
                            placeholder="-"
                            value={project?.subdivision?.address || selectedTemplate?.subdivision?.address || ""}
                            readOnly={true}
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <Row className="align-items-center">
                        <Col>
                          <Input
                            {...register("code")}
                            className="field text-uppercase"
                            label="Project ID"
                            required
                            placeholder="A-SCR040219-082"
                            maxLength={20}
                            errorMessage={errors.code?.message}
                            readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                          />
                        </Col>
                        <Col xs="auto" className="pt-3">
                          <label className="checkbox-label">
                            <input {...register("include_to_allocations")} className="field--checkbox" type="checkbox" />
                            <div className="checkbox mr-2">
                              <SvgIcon name="check" />
                            </div>
                            Enable direct labor allocation
                          </label>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Controller
                            name="project_type"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Project Type"
                                placeholder="Select a Project Type"
                                required
                                options={project_types}
                                noOptionsMessage={() => "No project types available."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption, actionMeta) => {
                                  field.onChange(newOption?.value);

                                  if (actionMeta.action == "select-option") {
                                    if (newOption.value == ProjectType.House) {
                                      if (selectedTemplate) {
                                        documentsFromTemplate(project, selectedTemplate).then((documents) => {
                                          setValue("documents_attributes", documents);
                                        });
                                      }
                                    }

                                    setValue(
                                      "code",
                                      `${newOption.value == ProjectType.House ? "A" : "B"}-${selectedTemplate.subdivision.code}${phase.padStart(
                                        2,
                                        "0"
                                      )}${block.padStart(2, "0")}${lot.padStart(2, "0")}-000`
                                    );
                                  } else {
                                    setValue("documents_attributes", project.documents_attributes);
                                  }
                                }}
                                value={project_types.find((project_type) => project_type.value == field.value) || null}
                                errorMessage={errors.project_type?.message}
                                disabled={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="unit_type"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Project Unit"
                                placeholder="Select a Unit Type"
                                required
                                options={unit_types}
                                noOptionsMessage={() => "No unit types available."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption, actionMeta) => {
                                  field.onChange(newOption?.value);
                                  if (actionMeta.action == "select-option") {
                                    if (newOption.value == UnitType.Standard) {
                                      if (selectedTemplate) {
                                        documentsFromTemplate(project, selectedTemplate).then((documents) => {
                                          setValue("documents_attributes", documents);
                                        });
                                      }
                                    }
                                  } else {
                                    setValue("project_type", "");
                                    resetField("documents_attributes");
                                  }
                                }}
                                value={unit_types.find((unit_type) => unit_type.value == field.value) || null}
                                errorMessage={errors.unit_type?.message}
                                disabled={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                              />
                            )}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Input
                            {...register("customer_first_name")}
                            className="field"
                            label="Customer First Name"
                            required
                            placeholder="John"
                            maxLength={100}
                            errorMessage={errors.customer_first_name?.message}
                            readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                          />
                        </Col>
                        <Col>
                          <Input
                            {...register("customer_last_name")}
                            className="field"
                            label="Customer Last Name"
                            required
                            placeholder="Doe"
                            maxLength={100}
                            errorMessage={errors.customer_last_name?.message}
                            readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Controller
                            name="finance_type"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                label="Financing"
                                placeholder="Select Financing"
                                options={finance_types}
                                noOptionsMessage={() => "No financing types available."}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                onChange={(newOption) => {
                                  field.onChange(newOption?.value);
                                }}
                                value={finance_types.find((finance_type) => finance_type.value == field.value) || null}
                                errorMessage={errors.finance_type?.message}
                                disabled={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                              />
                            )}
                          />
                        </Col>
                        <Col>
                          <Controller
                            name="equity"
                            control={control}
                            render={({ field }) => (
                              <FieldGroup label="Equity" errorMessage={errors.equity?.message}>
                                <IMaskInput
                                  mask="000.00"
                                  padFractionalZeros={true}
                                  value={field.value?.toString()}
                                  unmask={false}
                                  inputRef={field.ref}
                                  onAccept={(value) => {
                                    field.onChange(value);
                                  }}
                                  className="field"
                                  placeholder="0.12"
                                  min={0}
                                  max={100}
                                  readOnly={project.status != ProjectStatus.Draft && project.status != ProjectStatus.Pending}
                                />
                              </FieldGroup>
                            )}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Card.Body>
            </TabPanel>
            <TabPanel>
              <ProjectPropertiesForm
                project={project}
                template_id={selectedTemplate?.id || project?.template_id}
                users={users}
                foremen={foremen}
                warehouses={warehouses}
                allocation_rules={allocation_rules}
                can_edit={can_edit}
              />
            </TabPanel>
            <TabPanel>
              {project.status == ProjectStatus.Draft || project.status == ProjectStatus.Pending ? (
                <ProjectParticularsForm
                  project={project}
                  inventory_items={inventory_items}
                  isLoading={isInventoryLoading}
                  particular_types={particular_types}
                />
              ) : (
                <ProjectParticulars
                  project={project}
                  inventory_items={inventory_items}
                  isLoading={isInventoryLoading}
                  particular_types={particular_types}
                />
              )}
            </TabPanel>
            {project.status != ProjectStatus.Draft && (
              <>
                <TabPanel>
                  <ProjectTasksForm project={project} projectTasks={projectTasks} setProjectTasks={setProjectTasks} />
                </TabPanel>
                <TabPanel>
                  <ProjectCostBudget project={project} />
                </TabPanel>
                <TabPanel>
                  <ProjectRequisitions
                    project={project}
                    task={task}
                    inventory_items={inventory_items}
                    isLoading={isInventoryLoading}
                    can_approve_reject={can_approve_reject}
                    can_delete={can_delete}
                    can_request={can_request}
                  />
                </TabPanel>
                <TabPanel>
                  <ProjectReturns project={project} states={states} sources={sources} />
                </TabPanel>
                <TabPanel>
                  <ProjectChangeOrders project={project} is_admin_or_architect={is_admin_or_architect} />
                </TabPanel>
                <TabPanel>
                  <ProjectChangeOrderItems project={project} />
                </TabPanel>
                <TabPanel>
                  <ProjectChangeOrderRequisitions
                    project={project}
                    is_admin_or_architect={can_edit}
                    is_engineer={is_engineer}
                    can_approve_reject={can_approve_reject}
                    can_delete={can_delete}
                  />
                </TabPanel>
                <TabPanel>
                  <ProjectPayrolls project={project} />
                </TabPanel>
              </>
            )}
          </Tabs>
        </Form>
      </FormProvider>
      <Modal dialogClassName="modal-dialog-centered" show={showCompleteConfirmationModal}>
        <Modal.Header>{`Complete Project: ${project?.code}`}</Modal.Header>
        <Modal.Body>
          {project?.has_open_transactions ? (
            <p className="alert--danger">Project can't be completed because there are still open transactions</p>
          ) : (
            `Are you sure you want to complete project: ${project?.code}?`
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowCompleteConfirmationModal(!showCompleteConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          {!project?.has_open_transactions && (
            <Button
              className="button--success"
              icon="save"
              title="Yes, complete project."
              disabled={isSubmitting || isValidating}
              onClick={() => {
                setShowCompleteConfirmationModal(!showCompleteConfirmationModal);
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showCancelConfirmationModal}>
        <Modal.Header>{`Cancel Project: ${project?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to cancel project: ${project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowCancelConfirmationModal(!showCancelConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="save"
            title="Yes, cancel project."
            disabled={isSubmitting || isValidating}
            onClick={() => {
              setShowCancelConfirmationModal(!showCancelConfirmationModal);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showPendingConfirmationModal}>
        <Modal.Header>{`Save Project as Pending: ${project?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to save project as pending: ${project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPendingConfirmationModal(!showPendingConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--pending"
            icon="pending"
            title="Yes, save project as pending."
            disabled={isSubmitting || isValidating}
            onClick={() => {
              setShowPendingConfirmationModal(!showPendingConfirmationModal);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showOngoingConfirmationModal}>
        <Modal.Header>{`Mark Project as Ongoing: ${project?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to mark project as ongoing: ${project?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowOngoingConfirmationModal(!showOngoingConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="on-going"
            title="Yes, mark project as ongoing."
            disabled={isSubmitting || isValidating}
            onClick={() => {
              setShowOngoingConfirmationModal(!showOngoingConfirmationModal);
              handleSubmit(async (data) => await save(data), onError)();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showPunchlistingConfirmationModal}>
        <Modal.Header>{`Submit Project for Punchlist: ${project?.code}`}</Modal.Header>
        <Modal.Body>
          {project?.open_transactions ? (
            <p className="alert--danger">
              {project?.open_transaction_message ||
                "Project can't be submitted for punchlisting because there are still open transactions."}
            </p>
          ) : (
            <>
              <p>{`Are you sure you want to submit project for punchlist: ${project?.code}?`}</p>
              <p>All tasks will be closed.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPunchlistingConfirmationModal(!showPunchlistingConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          {!project?.open_transactions && (
            <Button
              className="button--success"
              icon="on-going"
              title="Yes, submit project for punchlist."
              disabled={isSubmitting || isValidating}
              onClick={() => {
                setShowPunchlistingConfirmationModal(!showPunchlistingConfirmationModal);
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      {/* {console.log(is_true)}

      <AttentionModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
        }}
      />   */}
    </React.Fragment>
  );
};

export default ProjectForm;
